"use client"

import { useState } from "react"
import Link from "next/link"
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { Locale } from "@/lib/i18n"

const copy = {
  tr: {
    label: "Teklif Al",
    sending: "Gönderiliyor...",
    sent: "Teklif talebiniz alındı",
    sentHint: "Projenizin detayları ekibimize iletildi. En kısa sürede sizinle iletişime geçeceğiz.",
    error: "Talebiniz gönderilemedi. Lütfen tekrar deneyin.",
  },
  en: {
    label: "Get Quote",
    sending: "Sending...",
    sent: "Quote request received",
    sentHint: "Your project details have been sent to our team. We will get in touch with you shortly.",
    error: "Your request could not be sent. Please try again.",
  },
}

// Sends the current analysis to our team in place (no redirect, no extra form)
export function RequestQuoteButton({ shareToken, locale, className = "" }: { shareToken: string | null; locale: Locale; className?: string }) {
  const t = copy[locale]
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle")
  const [error, setError] = useState("")

  // Without a saved analysis there is nothing to send: fall back to the quote form
  if (!shareToken) {
    return (
      <Button asChild size="lg" className={`bg-[#034AA6] hover:bg-[#034AA6]/90 text-white h-12 text-base ${className}`}>
        <Link href={locale === "en" ? "/en/quote-request" : "/teklif-al"}>
          {t.label}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Link>
      </Button>
    )
  }

  const send = async () => {
    setState("sending")
    setError("")
    try {
      const res = await fetch("/api/ai/request-quote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shareToken, locale }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || t.error)
      setState("sent")
    } catch (e) {
      setError(e instanceof Error ? e.message : t.error)
      setState("error")
    }
  }

  if (state === "sent") {
    return (
      <div className={`flex items-start gap-3 rounded-lg border border-[#034AA6]/30 bg-[#034AA6]/5 px-4 py-3 ${className}`} role="status">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#034AA6]" />
        <div>
          <p className="font-semibold text-[#034AA6]">{t.sent}</p>
          <p className="text-sm text-slate-600">{t.sentHint}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <Button onClick={send} disabled={state === "sending"} size="lg" className="w-full bg-[#034AA6] hover:bg-[#034AA6]/90 text-white h-12 text-base">
        {state === "sending" ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t.sending}
          </>
        ) : (
          <>
            {t.label}
            <ArrowRight className="ml-2 h-5 w-5" />
          </>
        )}
      </Button>
      {state === "error" && <p className="text-sm text-red-600">{error}</p>}
    </div>
  )
}
